import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import KeyResultForm, { KeyResultDraft } from '../components/KeyResultForm.tsx';
import { createObjective, listObjectives, updateObjective } from '../services/okrApi.ts';
import { formatDate, normalizeDate } from '../utils/dateUtils.ts';
import './Objectives.css';

interface Objective {
  id: number;
  title: string;
  description: string;
  period_type: 'annual' | 'quarterly';
  period_start: string;
  period_end: string;
  status: 'active' | 'archived';
}

const Objectives: React.FC = () => {
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingObjective, setEditingObjective] = useState<Objective | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    period_type: 'annual' as 'annual' | 'quarterly',
    period_start: '',
    period_end: '',
  });
  const [keyResults, setKeyResults] = useState<KeyResultDraft[]>([
    { title: '', target_value: 0, unit: '' },
    { title: '', target_value: 0, unit: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [archivingId, setArchivingId] = useState<number | null>(null);

  const fetchObjectives = async () => {
    try {
      const response = await listObjectives();
      setObjectives(response);
    } catch (error) {
      console.error('Erro ao buscar objetivos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObjectives();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      period_type: 'annual',
      period_start: '',
      period_end: '',
    });
    setKeyResults([
      { title: '', target_value: 0, unit: '' },
      { title: '', target_value: 0, unit: '' },
    ]);
    setEditingObjective(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingObjective) {
        await updateObjective(editingObjective.id, {
          title: formData.title,
          description: formData.description,
          period_type: formData.period_type,
          period_start: formData.period_start || undefined,
          period_end: formData.period_end || undefined,
        });
      } else {
        if (keyResults.length < 2 || keyResults.length > 5) {
          alert('Adicione entre 2 e 5 Key Results.');
          return;
        }
        await createObjective({
          title: formData.title,
          description: formData.description,
          period_type: formData.period_type,
          period_start: formData.period_start || undefined,
          period_end: formData.period_end || undefined,
          key_results: keyResults,
        });
      }
      setShowForm(false);
      resetForm();
      fetchObjectives();
    } catch (error) {
      console.error('Erro ao salvar objetivo:', error);
    } finally {
      setSaving(false);
    }
  };

  const archiveObjective = async (objective: Objective) => {
    setArchivingId(objective.id);
    try {
      await updateObjective(objective.id, { status: 'archived' });
      fetchObjectives();
    } catch (error) {
      console.error('Erro ao arquivar objetivo:', error);
    } finally {
      setArchivingId(null);
    }
  };

  const restoreObjective = async (objective: Objective) => {
    setArchivingId(objective.id);
    try {
      await updateObjective(objective.id, { status: 'active' });
      fetchObjectives();
    } catch (error) {
      console.error('Erro ao reativar objetivo:', error);
    } finally {
      setArchivingId(null);
    }
  };

  const startEditObjective = (objective: Objective) => {
    setEditingObjective(objective);
    setShowForm(true);
    setFormData({
      title: objective.title,
      description: objective.description,
      period_type: objective.period_type,
      period_start: normalizeDate(objective.period_start),
      period_end: normalizeDate(objective.period_end),
    });
  };

  if (loading) {
    return (
      <div className="objectives">
        <h1>Metas pessoais</h1>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="objectives">
      <div className="page-header">
        <div>
          <h1>OKRs pessoais</h1>
          <p>Objetivos claros com acesso rapido a edicao e arquivamento.</p>
        </div>
        <div className="page-header-actions">
          <button className="primary-button" onClick={() => setShowForm(true)}>
            <img src="/add.svg" alt="" className="button-icon" />
            Novo objetivo
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingObjective ? 'Editar objetivo' : 'Novo objetivo'}</h3>
              <button
                className="icon-button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                title="Fechar"
              >
                <img src="/x.svg" alt="Fechar" className="icon" />
              </button>
            </div>
            <form className="objective-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="objective-title">Título *</label>
                <input
                  type="text"
                  id="objective-title"
                  placeholder="Título do objetivo"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="objective-description">Descrição</label>
                <input
                  type="text"
                  id="objective-description"
                  placeholder="Descrição"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="objective-period-type">Período</label>
                  <select
                    id="objective-period-type"
                    value={formData.period_type}
                    onChange={(e) => setFormData({ ...formData, period_type: e.target.value as 'annual' | 'quarterly' })}
                  >
                    <option value="annual">Anual</option>
                    <option value="quarterly">Trimestral</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="objective-period-start">Início</label>
                  <input
                    type="date"
                    id="objective-period-start"
                    value={formData.period_start}
                    onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="objective-period-end">Fim</label>
                  <input
                    type="date"
                    id="objective-period-end"
                    value={formData.period_end}
                    onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                  />
                </div>
              </div>

              {!editingObjective && (
                <>
                  <h4>Key Results</h4>
                  <KeyResultForm keyResults={keyResults} onChange={setKeyResults} />
                </>
              )}

              <div className="form-actions">
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Salvando...' : (editingObjective ? 'Salvar objetivo' : 'Criar objetivo')}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    resetForm();
                    setShowForm(false);
                  }}
                  disabled={saving}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="objectives-content">
        <div className="objectives-filters">
          <div className="toggle-container">
            <label className="toggle-label" htmlFor="toggle-archived">
              Exibir arquivados
            </label>
            <label className="toggle-switch">
              <input
                id="toggle-archived"
                type="checkbox"
                checked={showArchived}
                onChange={(event) => setShowArchived(event.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>

        {objectives.filter((objective) => showArchived || objective.status === 'active').length === 0 ? (
          <div className="empty-state">
            <p>Nenhum objetivo cadastrado ainda.</p>
          </div>
        ) : (
          <div className="objective-list">
            {objectives
              .filter((objective) => showArchived || objective.status === 'active')
              .map((objective) => (
              <div key={objective.id} className="objective-card">
                <div className="objective-header">
                  <h3>{objective.title}</h3>
                  <div className="objective-actions">
                    <button
                      className="icon-button edit-button"
                      onClick={() => startEditObjective(objective)}
                      title="Editar objetivo"
                    >
                      <img src="/edit.svg" alt="Editar" className="icon" />
                    </button>
                    <Link className="icon-button view-button" to={`/objectives/${objective.id}`} title="Ver Key Results">
                      <img src="/target.svg" alt="Key Results" className="icon" />
                    </Link>
                    {objective.status !== 'archived' ? (
                      <button
                        className="icon-button archive-button"
                        onClick={() => archiveObjective(objective)}
                        title="Arquivar"
                        disabled={archivingId === objective.id}
                      >
                        {archivingId === objective.id ? '...' : <img src="/delete.svg" alt="Arquivar" className="icon" />}
                      </button>
                    ) : (
                      <button
                        className="icon-button restore-button"
                        onClick={() => restoreObjective(objective)}
                        title="Reativar"
                        disabled={archivingId === objective.id}
                      >
                        {archivingId === objective.id ? '...' : <img src="/undo.svg" alt="Reativar" className="icon" />}
                      </button>
                    )}
                  </div>
                </div>
                <p>{objective.description}</p>
                <div className="objective-meta">
                  <span>Período: {objective.period_type === 'annual' ? 'Anual' : 'Trimestral'}</span>
                  <span>
                    {formatDate(normalizeDate(objective.period_start))} - {formatDate(normalizeDate(objective.period_end))}
                  </span>
                  <span>{objective.status === 'active' ? 'Ativo' : 'Arquivado'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default Objectives;
